import React, { useEffect, useMemo } from "react";
import { Form, Row, Col, Alert, message, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomInput from "../../../components/CustomInput/Input";
import CustomButton from "../../../components/CustomButton/Button";
import {
  nameRules,
  phoneNumberRules,
  emailRules,
} from "../../../helpers/helpers";
import { updateSupplier } from "../../../redux/slices/supplierSlice";
import {
  extractBusinessEmails,
  extractBusinessPhones,
} from "./NewSupplierModal";

const { Text } = Typography;

const EditSupplierModal = ({ open, supplier, onClose, onSuccess, businessDetails }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { submitLoading, submitError } = useSelector((state) => state.supplier);
  const user = useSelector((state) => state.auth?.user);
  const busDetailedViewData = useSelector(
    (state) => state.profile1?.busDetailedViewData
  );

  const registeredBusinessEmails = useMemo(() => {
    return extractBusinessEmails(businessDetails || busDetailedViewData, user);
  }, [businessDetails, busDetailedViewData, user]);

  const registeredBusinessPhones = useMemo(() => {
    return extractBusinessPhones(businessDetails || busDetailedViewData, user);
  }, [businessDetails, busDetailedViewData, user]);

  useEffect(() => {
    if (supplier && open) {
      form.setFieldsValue({
        supplierName: supplier.supplierName || "",
        pocName: supplier.pocName || "",
        pocMobile: supplier.pocMobile || "",
        pocEmail: supplier.pocEmail || "",
        pan: supplier.pan || "",
        gstin: supplier.gstin || "",
        accNum: supplier.accNum || "",
        accHolderName: supplier.accHolderName || "",
        ifsc: supplier.ifsc || "",
        bankName: supplier.bankName || "",
        branchName: supplier.branchName || "",
      });
    }
  }, [supplier, open, form]);

  const handleFinish = async (values) => {
    const cleanEmail = (values.pocEmail || "").toLowerCase().trim();
    const cleanMobile = String(values.pocMobile || "").replace(/\D/g, "").slice(-10);

    if (registeredBusinessEmails.includes(cleanEmail)) {
      form.setFields([
        {
          name: "pocEmail",
          errors: [
            "Supplier email address cannot be the same as the registered business email address.",
          ],
        },
      ]);
      message.error(
        "Supplier email address cannot be the same as the registered business email address."
      );
      return;
    }

    if (cleanMobile.length === 10 && registeredBusinessPhones.includes(cleanMobile)) {
      form.setFields([
        {
          name: "pocMobile",
          errors: [
            "Supplier mobile number cannot be the same as the registered business mobile number.",
          ],
        },
      ]);
      message.error(
        "Supplier mobile number cannot be the same as the registered business mobile number."
      );
      return;
    }

    const payload = {
      supplierName: supplier?.supplierName,
      pocName: values.pocName?.trim(),
      pocMobile: values.pocMobile?.trim(),
      pocEmail: values.pocEmail?.toLowerCase().trim(),
      accNum: supplier?.accNum,
      accHolderName: supplier?.accHolderName,
      ifsc: supplier?.ifsc,
      bankName: supplier?.bankName || undefined,
      branchName: supplier?.branchName || undefined,
    };

    const res = await dispatch(
      updateSupplier({ supplierId: supplier.supplierId, payload })
    );

    if (
      res?.meta?.requestStatus === "fulfilled" &&
      (res?.payload?.statusCode === 200 ||
        res?.payload?.data?.msg ||
        res?.payload?.msg)
    ) {
      message.success(`Supplier "${payload.supplierName || supplier.supplierName}" updated successfully.`);
      onSuccess?.();
      onClose();
    } else {
      const errMsg =
        res?.payload?.response?.data?.message ||
        res?.payload?.response?.data?.error ||
        res?.payload?.message ||
        res?.payload ||
        "Failed to update supplier.";
      const displayErr =
        typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg);
      if (displayErr.toLowerCase().includes("email")) {
        form.setFields([{ name: "pocEmail", errors: [displayErr] }]);
      } else if (
        displayErr.toLowerCase().includes("phone") ||
        displayErr.toLowerCase().includes("mobile")
      ) {
        form.setFields([{ name: "pocMobile", errors: [displayErr] }]);
      }
      message.error(displayErr);
    }
  };

  return (
    <CustomModal
      title={`Edit Supplier - ${supplier?.supplierName || ""}`}
      open={open}
      centered
      onCancel={onClose}
      width={720}
      content={
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          style={{ paddingTop: 12 }}
        >
          {submitError && (
            <Alert
              type="error"
              message={
                typeof submitError === "string"
                  ? submitError
                  : JSON.stringify(submitError)
              }
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <div
            style={{
              background: "#f8f9fc",
              padding: "12px 16px",
              borderRadius: 6,
              marginBottom: 16,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>PAN (Fixed KYC):</Text>{" "}
              <Text strong>{supplier?.pan || "N/A"}</Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>GSTIN (Fixed KYC):</Text>{" "}
              <Text strong>{supplier?.gstin || "N/A"}</Text>
            </div>
          </div>

          <Row gutter={[16, 0]}>
            {/* Supplier Name - Read-only */}
            <Col xs={24} md={12}>
              <CustomInput
                label="Supplier Name"
                name="supplierName"
                placeholder="Enter Supplier Name"
                disabled
              />
            </Col>

            {/* POC Name - Editable */}
            <Col xs={24} md={12}>
              <CustomInput
                label="Contact Person (POC Name)"
                name="pocName"
                placeholder="Enter POC Name"
                isRequired
                rules={nameRules("POC")}
              />
            </Col>

            {/* POC Mobile - Editable */}
            <Col xs={24} md={12}>
              <CustomInput
                label="POC Mobile Number"
                name="pocMobile"
                placeholder="Enter 10-digit mobile number"
                addonBefore="+91"
                isRequired
                rules={[
                  ...phoneNumberRules("POC"),
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const clean = String(value).replace(/\D/g, "").slice(-10);
                      if (
                        clean.length === 10 &&
                        registeredBusinessPhones.includes(clean)
                      ) {
                        return Promise.reject(
                          new Error(
                            "Supplier mobile number cannot be the same as the registered business mobile number."
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                    validateTrigger: ["onBlur", "onChange"],
                  },
                ]}
              />
            </Col>

            {/* POC Email - Editable */}
            <Col xs={24} md={12}>
              <CustomInput
                label="POC Email"
                name="pocEmail"
                type="email"
                placeholder="Enter POC email address"
                isRequired
                rules={[
                  ...emailRules("POC"),
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const clean = (value || "").toLowerCase().trim();
                      if (registeredBusinessEmails.includes(clean)) {
                        return Promise.reject(
                          new Error(
                            "Supplier email address cannot be the same as the registered business email address."
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                    validateTrigger: ["onBlur", "onChange"],
                  },
                ]}
              />
            </Col>

            {/* Bank Account Number - Read-only */}
            <Col xs={24} md={12}>
              <CustomInput
                label="Bank Account Number"
                name="accNum"
                placeholder="Enter Account Number"
                disabled
              />
            </Col>

            {/* Account Holder Name - Read-only */}
            <Col xs={24} md={12}>
              <CustomInput
                label="Account Holder Name"
                name="accHolderName"
                placeholder="Enter Account Holder Name"
                disabled
              />
            </Col>

            {/* IFSC Code - Read-only */}
            <Col xs={24} md={12}>
              <CustomInput
                label="IFSC Code"
                name="ifsc"
                placeholder="e.g. HDFC0001234"
                disabled
              />
            </Col>

            {/* Bank Name - Read-only */}
            <Col xs={24} md={12}>
              <CustomInput
                label="Bank Name"
                name="bankName"
                placeholder="Bank Name"
                disabled
              />
            </Col>

            {/* Branch Name - Read-only */}
            <Col xs={24} md={12}>
              <CustomInput
                label="Branch Name"
                name="branchName"
                placeholder="Branch Name"
                disabled
              />
            </Col>
          </Row>

          <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
            <Col>
              <CustomButton onClick={onClose} disabled={submitLoading}>
                Cancel
              </CustomButton>
            </Col>
            <Col>
              <CustomButton
                type="primary"
                htmlType="submit"
                loading={submitLoading}
                style={{ background: "#294DD8", minWidth: 120 }}
              >
                Save Changes
              </CustomButton>
            </Col>
          </Row>
        </Form>
      }
    />
  );
};

export default EditSupplierModal;
